import * as THREE from 'three';

export class SceneTextureCache {
  constructor(options = {}) {
    this.imageLoader = options.imageLoader ?? loadImageResource;
    this.textureFactory = options.textureFactory ?? createTextureFromImage;
    this.fallbackTextureFactory =
      options.fallbackTextureFactory ?? createFallbackTexture;
    this.entries = new Map();
    this.ownedTextures = new Set();
    this.fallbackTexture = null;
    this.disposed = false;
  }

  load(resource) {
    const key = resource?.textureKey || resource?.src;

    if (!key || !resource?.src) {
      return Promise.resolve(this.getFallbackTexture());
    }

    if (this.entries.has(key)) {
      return this.entries.get(key);
    }

    const promise = this.imageLoader(resource)
      .then((image) => this.createTexture(image, resource))
      .catch(() => this.getFallbackTexture());

    this.entries.set(key, promise);
    return promise;
  }

  dispose() {
    this.disposed = true;
    this.entries.clear();

    for (const texture of this.ownedTextures) {
      texture.dispose?.();
    }

    this.ownedTextures.clear();
    this.fallbackTexture = null;
  }

  createTexture(image, resource) {
    if (this.disposed) {
      return this.getFallbackTexture();
    }

    const texture = this.textureFactory(image, resource);
    this.ownedTextures.add(texture);
    return texture;
  }

  getFallbackTexture() {
    if (!this.fallbackTexture) {
      this.fallbackTexture = this.fallbackTextureFactory();
      this.ownedTextures.add(this.fallbackTexture);
    }

    return this.fallbackTexture;
  }
}

function loadImageResource(resource) {
  return loadWithFallback(resource, false);
}

function loadWithFallback(resource, fallbackAttempt) {
  const src = fallbackAttempt ? resource.fallbackSrc : resource.src;
  const useResponsiveFields = !fallbackAttempt;

  return loadImage({
    src,
    srcset: useResponsiveFields ? resource.srcset : '',
    sizes: useResponsiveFields ? resource.sizes : '',
    crossOrigin: resource.crossOrigin,
    referrerPolicy: resource.referrerPolicy,
  }).catch((error) => {
    if (
      !fallbackAttempt &&
      resource.fallbackSrc &&
      resource.fallbackSrc !== resource.src
    ) {
      return loadWithFallback(resource, true);
    }

    throw error;
  });
}

function loadImage({ src, srcset, sizes, crossOrigin, referrerPolicy }) {
  return new Promise((resolve, reject) => {
    const image = new Image();

    if (crossOrigin !== false && !isDataLikeUrl(src)) {
      image.crossOrigin = crossOrigin ?? 'anonymous';
    }

    if (referrerPolicy) {
      image.referrerPolicy = referrerPolicy;
    }

    image.decoding = 'async';
    image.onload = async () => {
      try {
        if (image.decode) {
          await image.decode();
        }
      } catch {}

      resolve(image);
    };
    image.onerror = () => reject(new Error(`Failed to load image: ${src}`));

    if (srcset) {
      image.srcset = srcset;
    }

    if (sizes) {
      image.sizes = sizes;
    }

    image.src = src;
  });
}

function createTextureFromImage(image) {
  const texture = new THREE.Texture(image);
  texture.needsUpdate = true;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  return texture;
}

function createFallbackTexture() {
  const data = new Uint8Array([
    232, 228, 220, 255, 214, 208, 198, 255, 214, 208, 198, 255, 232, 228, 220,
    255,
  ]);
  const texture = new THREE.DataTexture(data, 2, 2, THREE.RGBAFormat);
  texture.needsUpdate = true;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.NearestFilter;
  texture.magFilter = THREE.NearestFilter;
  return texture;
}

function isDataLikeUrl(src = '') {
  return src.startsWith('data:') || src.startsWith('blob:');
}
